import { IconButton, Stack, Typography } from '@mui/material';
import RowTypography from 'components/Table/RowTypography';
import React, { useEffect, useState } from 'react';
import { ReactComponent as ClockIcon } from '../../assets/time-history.svg';
import CloseIcon from '@mui/icons-material/Close';
import { CustomizedDataGrid } from 'components/customized_data_grid';
import LoaderComponent from 'components/Loader';
import { getCyclicExecutions } from 'service/service';
import StatusChip from 'components/Table/StatusChip';
import { Link } from 'react-router-dom';
import { PLATFORM_ID_TO_NAME, PLATFORM_NAMES } from './constant';
import { convertMsToHHMMSS, formatExecutionTime } from 'utils/common-util';
import { useTheme } from "@mui/material/styles";


interface CyclicExecutionsProps {
  startDate: string;
  endDate: string;
  curRow: any;
  setIsCyclicExeData: any;
}

type ColumnConfig = { name: string; label: string };

interface CellOptions {
  platformId: any;
  treatSucceededAsSuccess?: boolean;
}

/**
 * Normalize status & value for StatusChip
 */
function getStatusChipProps(
  rawStatus: unknown,
  treatSucceededAsSuccess: boolean = false
) {
  if (rawStatus === undefined || rawStatus === null) {
    return { status: '--', value: '--' };
  }

  const statusStr =
  typeof rawStatus === 'string' || typeof rawStatus === 'number'
    ? String(rawStatus)
    : '';
  const lower = statusStr.toLowerCase();

  const isSuccess =
    lower === 'successful' ||
    lower === 'successfull' ||
    (treatSucceededAsSuccess && lower === 'succeeded');

  if (isSuccess) {
    return { status: 'Success', value: 'Success' };
  }

  if (lower === 'failure') {
    return { status: 'Failed', value: 'Failed' };
  }

  return { status: statusStr, value: statusStr };
}

/**
 * Get the actual cell content (inside RowTypography)
 * – avoids nested ternaries in JSX
 */
function getCellContent(
  row: any,
  columnKey: string,
  options: CellOptions
): React.ReactNode {
  const { treatSucceededAsSuccess = false, platformId } = options;
  const value = row[columnKey];

  if (columnKey === 'jobStatus') {
    const { status, value: displayValue } = getStatusChipProps(
      value,
      treatSucceededAsSuccess
    );
    return <StatusChip status={status} value={displayValue} />;
  }
    const formatMsToMinutes = (ms: number) => {
      if (!ms || ms <= 0) return '--';
      return convertMsToHHMMSS(ms);
    };
    
  if (columnKey === 'avgRunTime') {
    return formatMsToMinutes(Number(row.avgRunTime));
  }

  if (columnKey === 'totalExecutionTime') {
   
    return formatExecutionTime(row)
  }

  return value ?? '--';
}

const columnNamesAdf: ColumnConfig[] = [
  { name: 'execId', label: 'Pipeline Run ID' },
  { name: 'actualStartTime', label: 'Act. Start Time (CST)' },
  { name: 'actualEndTime', label: 'Act. End Time (CST)' },
  { name: 'jobStatus', label: 'Status' },
  { name: 'avgRunTime', label: 'Avg Exec. Time' },
  { name: 'totalExecutionTime', label: 'Total Exec. Time' }
];

interface AdfSqlFormatterProps {
  row: any;
  column: any;
}

interface FormatterProps {
  row: any;
  column: any;
  platformId?: number;
}

const AdfFormatter: React.FC<AdfSqlFormatterProps> = ({ row, column }) => {
  const isExecId = column.key === 'execId';

  if (!isExecId) {
    return (
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <RowTypography isSuccessor={!row[column.key]}>
          {getCellContent(row, column.key, {platformId: PLATFORM_NAMES.ADF,treatSucceededAsSuccess: true})}
        </RowTypography>
      </Stack>
    );
  }

  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      {row.adfUrl ? (
        <Link to={row.adfUrl} target="_blank" state={row} style={{ textDecoration: 'underline' }}>
          <RowTypography isSuccessor={!row[column.key]} title={row[column.key]}>
            {row[column.key] ? row[column.key] : '--'}
          </RowTypography>
        </Link>
      ) : (
        <RowTypography isSuccessor={!row[column.key]} title={row[column.key]}>
          {row[column.key] ? row[column.key] : '--'}
        </RowTypography>
      )}
    </Stack>
  );
};

const SqlFormatter: React.FC<AdfSqlFormatterProps> = ({ row, column }) => {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <RowTypography isSuccessor={!row[column.key]}>
        {getCellContent(row, column.key, {platformId: PLATFORM_NAMES.SQL_AGENT_JOBS})}
      </RowTypography>
    </Stack>
  );
};

const ColumnFormatter: React.FC<FormatterProps> = ({ row, column, platformId }) => {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <RowTypography isSuccessor={!row[column.key]}>
        {getCellContent(row, column.key, {platformId})}
      </RowTypography>
    </Stack>
  );
};

const columnFormatterHelper = 
  (platformId: number) => 
  ({ row, column }: any) => (
  <ColumnFormatter row={row} column={column} platformId={platformId} />
);

const CyclicExecutions: React.FC<CyclicExecutionsProps> = ({
  startDate,
  endDate,
  curRow,
  setIsCyclicExeData
}: any) => {
  const defaultColumnNamesMDTDDH = [
    'Act. Start Time (CST)',
    'Act. End Time (CST)',
    'Avg Exec. Time',
    'Total Exec. Time',
    'Status'
  ];

  const defaultColumnNames = [
    // 'Exec Id',
    'Act. Start Time (CST)',
    'Act. End Time (CST)',
    'Avg Exec. Time',
    'Total Exec. Time',
    'Status'
  ];

  const defaultColumnNamesAdf = [
    'Pipeline Run ID',
    'Act. Start Time (CST)',
    'Act. End Time (CST)',
    'Avg Exec. Time',
    'Total Exec. Time',
    'Status'
  ];

  const defaultColumnNamesIics = [
    'Run ID',
    'Asset Type',
    'Location',
    'Act. Start Time (CST)',
    'Act. End Time (CST)',
    'Status',
    'Subtask Details',
    'Avg Exec. Time',
    'Total Exec. Time'
  ];

  const getDefaultColumnNames = (platformId: any) => {
    switch (platformId) {
      case PLATFORM_NAMES.MDT:
      case PLATFORM_NAMES.DDH:
        return defaultColumnNamesMDTDDH;

      case PLATFORM_NAMES.ADF:
        return defaultColumnNamesAdf;

      case PLATFORM_NAMES.IICS:
        return defaultColumnNamesIics;

      default:
        return defaultColumnNames;
    }
  };

  const columnNamesWithoutExeId: ColumnConfig[] = [
    { name: 'actualStartTime', label: 'Act. Start Time (CST)' },
    { name: 'actualEndTime', label: 'Act. End Time (CST)' },
    { name: 'jobStatus', label: 'Status' },
    { name: 'avgRunTime', label: 'Avg Exec. Time' },
    { name: 'totalExecutionTime', label: 'Total Exec. Time' }
  ];

  const columnNames: ColumnConfig[] = [
    // { name: 'execId', label: 'Exec Id' },
    { name: 'actualStartTime', label: 'Act. Start Time (CST)' },
    { name: 'actualEndTime', label: 'Act. End Time (CST)' },
    { name: 'jobStatus', label: 'Status' },
    { name: 'avgRunTime', label: 'Avg Exec. Time' },
    { name: 'totalExecutionTime', label: 'Total Exec. Time' }
  ];

  const columnNamesSql: ColumnConfig[] = [
    // { name: 'execId', label: 'Exec Id' },
    { name: 'stepId', label: 'Step ID' },
    { name: 'stepName', label: 'Step Name' },
    { name: 'actualStartTime', label: 'Act. Start Time (CST)' },
    { name: 'actualEndTime', label: 'Act. End Time (CST)' },
    { name: 'jobStatus', label: 'Status' },
    { name: 'avgRunTime', label: 'Avg Exec. Time' },
    { name: 'totalExecutionTime', label: 'Total Exec. Time' }
  ];

  const columnNamesIics: ColumnConfig[] = [
    { name: 'execId', label: 'Run ID' },
    { name: 'assetType', label: 'Asset Type' },
    { name: 'location', label: 'Location' },
    { name: 'actualStartTime', label: 'Act. Start Time (CST)' },
    { name: 'actualEndTime', label: 'Act. End Time (CST)' },
    { name: 'jobStatus', label: 'Status' },
    { name: 'subtasks', label: 'Subtask Details' },
    { name: 'avgRunTime', label: 'Avg Exec. Time' },
    { name: 'totalExecutionTime', label: 'Total Exec. Time' }
  ];

  const getTableColumns = (platformId: any) => {
    switch (platformId) {
      case PLATFORM_NAMES.MDT:
      case PLATFORM_NAMES.DDH:
      case PLATFORM_NAMES.WINDOWS_SERVER:
        return getColumnsWithoutExeId(platformId);

      case PLATFORM_NAMES.ADF:
        return getAdfColumns();

      case PLATFORM_NAMES.SQL_AGENT_JOBS:
        return getSQLColumns();

      case PLATFORM_NAMES.IICS:
        return getIicsColumns(platformId);

      default:
        return getColumns(platformId);
    }
  };

  const adfFormatterHelper = ({ row, column }: any) => (
    <AdfFormatter row={row} column={column} />
  );

  function getAdfColumns(): Array<any> {
    return columnNamesAdf.map((col: ColumnConfig) => ({
      key: col.name,
      name: col.label,
      formatter: adfFormatterHelper,
      // width: 300
    }));
  }

  const theme = useTheme();
  const colors = theme.palette;
  const [updateCyclicExec, setUpdateCyclicExec] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  async function cyclicExecFunc() {
    setLoading(true);
    const res = await getCyclicExecutions({
      cyclic: curRow.isCyclic,
      dataAssetName: curRow.dataAssetName,
      silverTableName: curRow.silverTable,
      jobId: curRow.jobId,
      fromDate: curRow.orderDate,
      platformId: curRow.platformId,
      scheduleInfo: curRow.scheduleInfo,
      triggerName: curRow.triggerName,
      startDate: curRow.startDate,
      endDate: endDate,
      pipelineParam: curRow.pipeline_param
    });
    if (res.success) {
      setLoading(false);
      setUpdateCyclicExec(res.data.cyclicExeData);
    }
  }

  //fetching data
  useEffect(() => {
    cyclicExecFunc();
  }, [curRow]);

  const sqlFormatterHelper = ({ row, column }: any) => (
    <SqlFormatter row={row} column={column} />
  );

  function getSQLColumns(): Array<any> {
    return columnNamesSql.map((col: ColumnConfig) => ({
      key: col.name,
      name: col.label,
      formatter: sqlFormatterHelper,
      // width: col.name === 'execId' || col.name === 'stepName' ? 300 : 200
    }));
  }

  function getColumnsWithoutExeId(platformId: number): Array<any> {
    const formatter = columnFormatterHelper(platformId);
    return columnNamesWithoutExeId.map((col: ColumnConfig) => ({
      key: col.name,
      name: col.label,
      formatter,
      // width: 200
    }));
  }

  function getIicsColumns(platformId: number): Array<any> {
    const formatter = columnFormatterHelper(platformId);
    return columnNamesIics.map((col: ColumnConfig) => ({
      key: col.name,
      name: col.label,
      formatter,
    }));
  }

  function getColumns(platformId: number): Array<any> {
    const formatter = columnFormatterHelper(platformId);
    return columnNames.map((col: ColumnConfig) => ({
      key: col.name,
      name: col.label,
      formatter,
      // width: col.name === 'execId' ? 300 : 200
    }));
  }

  return (
    <Stack direction="column" p={2} position='relative' sx={{ bgcolor: colors.backgroundLight.primary}}>
      <IconButton onClick={() => setIsCyclicExeData(false)} sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}>
        <CloseIcon sx ={{ color: colors.buttons.primary.solidBg}}/>
      </IconButton>
      <Stack direction="row" spacing={12} pt={1} pb={2} marginBottom={1} borderBottom={`0.5px solid ${colors.table.text}`}>
        <Stack>
          <Typography sx={{ color: colors.table.text }}>
            {curRow.platformId === PLATFORM_NAMES.ADF ? 'Pipeline Name' : 'Job Name'}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            <Typography component="span" sx={{ fontWeight: 'bold' }}>{curRow.jobName}</Typography>
          </Typography>
        </Stack>

        <Stack>
          <Typography sx={{ color: colors.table.text }}>
            Platform&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            <Typography component="span" sx={{ fontWeight: 'bold' }}>
              {curRow.platformId ? PLATFORM_ID_TO_NAME[curRow.platformId] : ' - '}
            </Typography>
          </Typography>
        </Stack>
        <Stack>
          <Typography sx={{ color: colors.table.text}}>
            Cyclic&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            <Typography component="span" sx={{ fontWeight: 'bold' }}>
              {curRow.platformId === PLATFORM_NAMES.ADF ? 'No' : curRow.isCyclic}
            </Typography>
          </Typography>
        </Stack>
      </Stack>
      {loading ? (
        <LoaderComponent />
      ) : (
        <CustomizedDataGrid
          title={
            <Stack direction="row" alignItems="center" spacing={1} mt="1rem">
              <ClockIcon />
              <Typography fontSize="16px">Recent Executions</Typography>
            </Stack>
          }
          tableColumns={getTableColumns(curRow.platformId)}
          tableData={updateCyclicExec}
          tableType="history-list"
          defaultColumnNames={getDefaultColumnNames(curRow.platformId)}
          autoSetDraggedColumns={false}
          listLength={updateCyclicExec.length}
          showExport={false}
          isDateRangeFilter={false}
          isDisabled={false}
          isRenderPagination={false}
          isShowMoreButton={false}
          style={{ maxHeight: '250px' }}
          rowHeight={40}
        />
      )}
    </Stack>
  );
};

export default CyclicExecutions;